/**
 * SDK 弹窗 Portal:把弹窗渲染逃出组件 Shadow DOM 到 document.body 级独立 shadow root。
 *
 * 背景:SDK 组件(<hotel-package-list> 等)渲染在各自 Shadow DOM 内。第三方挂件(客服
 * widget.js)挂在 document.body(light DOM、根层叠上下文)。Shadow 内的 position:fixed
 * 遮罩被困在 shadow host 的层叠上下文里,而 host 又常嵌在宿主页中间层叠上下文中,
 * 故无论 shadow 内 z-index 多高、或临时提升 host 的 z-index,都无法可靠盖住 body 级挂件。
 *
 * 解法:在 document.body 上挂一个 0×0 的 fixed 锚点 div,给它 attachShadow,注入与主
 * shadow 相同的 SDK_STYLE+SDK_STYLE_H5;弹窗用 createPortal 渲染进这个新 shadow。
 *  - 锚点是 body 直接子元素,在根层叠上下文中竞争 z-index,可稳定盖住 body 级挂件;
 *  - 自带独立 shadow root,注入样式里的 :host token 块对锚点生效 -> --hh-* 可用,
 *    样式完全封装,不泄漏宿主页也不被宿主页影响;复用同一份样式串,零漂移;
 *  - 锚点 0×0 overflow:visible,空载时不占面积不挡事件;遮罩/卡片本身 position:fixed
 *    相对视口(锚点无 transform,不构成 fixed 后代的包含块),不受锚点尺寸影响。
 *
 * 单例懒创建:首次取用时建 host+shadow+mount 并 append 到 body,之后复用同一 mount。
 */
import { SDK_STYLE } from './styles';
import { SDK_STYLE_H5 } from './styles.h5';

/** 弹窗 portal 宿主 z-index:取近 int32 上限,确保盖住任意 body 级挂件(含客服 widget)。 */
const PORTAL_Z_INDEX = 2147483646;

let portalMount: HTMLDivElement | null = null;

/**
 * 取(必要时创建)弹窗 portal 的 React 挂载点(body 级独立 shadow 内的 div)。
 * SSR/无 document 时返回 null,调用方据此跳过 portal(弹窗仅 SDK 客户端用例触发)。
 */
export function getSdkModalPortal(): HTMLDivElement | null {
  if (typeof document === 'undefined') return null;
  if (portalMount) return portalMount;

  const host = document.createElement('div');
  host.setAttribute('data-hh-modal-portal', '');
  // 0×0 fixed 锚点:仅作 shadow 载体与层叠定位,不占面积。inline 样式优先级高于注入样式
  // 中的 :host{all:initial},确保 position/尺寸/z-index 生效(z-index 需 position 非 static)。
  host.style.position = 'fixed';
  host.style.top = '0';
  host.style.left = '0';
  host.style.width = '0';
  host.style.height = '0';
  host.style.overflow = 'visible';
  host.style.zIndex = String(PORTAL_Z_INDEX);

  const shadow = host.attachShadow({ mode: 'open' });
  // 复用主 shadow 同款样式串:注入样式里的 :host token 块对锚点生效,弹窗 var(--hh-*) 可解析;
  // data-hh-sdk 标记与主 shadow 一致(分属不同 shadow,互不干扰)。
  const style = document.createElement('style');
  style.setAttribute('data-hh-sdk', '');
  style.textContent = SDK_STYLE + SDK_STYLE_H5;
  shadow.appendChild(style);

  const mount = document.createElement('div');
  shadow.appendChild(mount);

  document.body.appendChild(host);
  portalMount = mount;
  return portalMount;
}
